// src/components/yourpath/SettingsTab.tsx

import {Box, Divider, IconButton, Typography} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import React, {useEffect, useState} from 'react';
import {useForm, Controller} from 'react-hook-form';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../../redux/store';
import {
  setPrefix,
  setSuffix,
  setMinLength,
  setAnalyticsEngine,
} from '../../../redux/slice/projectFormSlice';
import ArrTextField from '../../common/arr-text-field/ArrTextField';
import ArrSingleSelectDropdown from '../../common/arr-select-dropdown/ArrSingleSelectDropdown';
import ArrTooltipWrapper from '../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import {t} from 'i18next';
import './CreateProjectPage.scss';
import {UserMappingItem} from '../../../api/types/project.type';
import {ReactComponent as CloseToast} from '../../../../src/assets/images/close-toast.svg';
import {ToastType} from '../../../shared/constants';
import ArrToast from '../../common/arr-toast/ArrToast';

// Define form data interface
interface SettingsFormData {
  prefix: string;
  suffix: string;
  minLength: string;
  analyticsEngine: {label: string; value: number} | null;
}

const SettingsTab: React.FC<{
  onFormValidationChange?: (isValid: boolean) => void;
  data: any;
}> = ({onFormValidationChange, data}) => {
  const dispatch = useDispatch();

  // Read from Redux
  const prefix = useSelector((state: RootState) => state.projectForm.prefix);
  const suffix = useSelector((state: RootState) => state.projectForm.suffix);
  const minLength = useSelector(
    (state: RootState) => state.projectForm.minLength
  );
  const analyticsEngine = useSelector(
    (state: RootState) => state.projectForm.analyticsEngine
  );

  const roleId = useSelector(
    (state: RootState) => state.authenticatedUser.user?.roleId
  );

  const [showToast, setShowToast] = useState(false);
  const [toastType, setToastType] = useState<ToastType>(ToastType.Error);
  const [message, setMessage] = useState('');

  const type = useSelector((state: RootState) => state.projectForm.type);
  const analyticsEngineList = useSelector(
    (state: RootState) => state.projectForm.analyticsEngineList
  );

  const sourceEngines =
    type === 'edit' ? analyticsEngineList : data?.analyticsEngines;

  const engineOptions = sourceEngines?.map((engine: UserMappingItem) => ({
    label: engine.value,
    value: engine.key,
  }));

  const {
    register,
    control,
    watch,
    setValue,
    getValues,
    formState: {errors, isValid},
  } = useForm<SettingsFormData>({
    mode: 'onChange',
    defaultValues: {
      prefix,
      suffix,
      minLength,
      analyticsEngine,
    },
  });

  useEffect(() => {
    if (
      data?.analyticsEngines &&
      type !== 'edit' &&
      roleId === -1 &&
      analyticsEngine === null
    ) {
      const selectedValue = data.analyticsEngines.find(
        (value: UserMappingItem) =>
          value.value.toLowerCase() ===
          t('projectManagement.settingsTab.consilioAnalytics').toLowerCase()
      );

      if (selectedValue) {
        const selectedAnalyticEngine = {
          value: selectedValue.key,
          label: selectedValue.value,
        };
        setValue('analyticsEngine', selectedAnalyticEngine);
      }
    }
  }, [data?.analyticsEngines]);

  // Sync watched values to Redux
  useEffect(() => {
    const subscription = watch((value) => {
      dispatch(setPrefix(value.prefix ?? ''));
      dispatch(setSuffix(value.suffix ?? ''));
      dispatch(setMinLength(value.minLength ?? ''));

      dispatch(setAnalyticsEngine(value.analyticsEngine));
    });
    return () => subscription.unsubscribe();
  }, [watch, dispatch]);

  // Report form validation status upward
  useEffect(() => {
    onFormValidationChange?.(isValid);
  }, [isValid, onFormValidationChange]);

  const validateMinNumberLength = () => {
    const minLength = getValues('minLength');

    if (Number(minLength) > 12) {
      setShowToast(true);
      setToastType(ToastType.Error);
      setMessage(t('projectManagement.settingsTab.minLengthError'));
      dispatch(setMinLength('8'));
      setValue('minLength', '8');
    }
  };

  return (
    <div
      className="settings-content"
      data-testid={`settings-tab-docid-format-docid-tooltip-analytics-engine-analytics-engine-tooltip`}
    >
      <div
        className="docid-section"
        data-testid={`settings-tab-docid-format-docid-tooltip`}
      >
        <Box
          className="docid-label"
          component="h1"
          data-testid={`settings-tab-docid-format-docid-tooltip-btn`}
        >
          <Typography
            tabIndex={0}
            role="button"
            className="typo-text subhead3_size assign-users-title"
            data-testid={`settings-tab-docid-format`}
          >
            {t('projectManagement.settingsTab.docidFormat')}
          </Typography>
          <ArrTooltipWrapper
            trigger={
              <IconButton aria-label="Info" sx={{padding: 0}}>
                <InfoOutlinedIcon fontSize="medium" className="info-icon" />
              </IconButton>
            }
          >
            {t('projectManagement.settingsTab.docidTooltip')}
          </ArrTooltipWrapper>
        </Box>

        <div className="docid-form" data-testid={`settings-tab-div`}>
          {/* Prefix */}
          <ArrTextField
            id="prefix-input"
            label={t('projectManagement.settingsTab.prefix')}
            dataTestId="settings-tab-prefix"
            state={errors.prefix ? 'error' : 'enabled'}
            supportText={errors.prefix?.message?.toString()}
            {...register('prefix', {
              pattern: {
                value: /^[A-Za-z0-9@\-_$%^+=~]*$/,
                message: t(
                  'projectManagement.createProjectModule.specialCharacterValidation'
                ),
              },
              maxLength: {
                value: 10,
                message: t('projectManagement.validation.maxLength', {
                  value: 10,
                }),
              },
            })}
            className="docid-field"
            slotProps={{
              input: {
                inputProps: {
                  maxLength: 10,
                },
              },
            }}
          />

          {/* Suffix */}
          <ArrTextField
            id="suffix-input"
            label={t('projectManagement.settingsTab.suffix')}
            dataTestId="settings-tab-suffix"
            state={errors.suffix ? 'error' : 'enabled'}
            supportText={errors.suffix?.message?.toString()}
            {...register('suffix', {
              pattern: {
                value: /^[A-Za-z0-9@\-_$%^+=~]*$/,
                message: t(
                  'projectManagement.createProjectModule.specialCharacterValidation'
                ),
              },
              maxLength: {
                value: 10,
                message: t('projectManagement.validation.maxLength', {
                  value: 10,
                }),
              },
            })}
            className="docid-field"
            slotProps={{
              input: {
                inputProps: {
                  maxLength: 10,
                },
              },
            }}
          />

          {/* MinLength */}
          <ArrTextField
            id="min-number-input"
            label={t('projectManagement.settingsTab.minNumber')}
            dataTestId="settings-tab-min-number"
            state={errors.minLength ? 'error' : 'enabled'}
            supportText={errors.minLength?.message?.toString()}
            {...register('minLength', {
              pattern: {
                value: /^\d+$/,
                message: t('projectManagement.validation.onlyNumbers'),
              },
              onBlur: () => {
                validateMinNumberLength();
              },
            })}
            onKeyDown={(e) => {
              if (
                !/\d/.test(e.key) &&
                e.key !== 'Backspace' &&
                e.key !== 'Tab'
              ) {
                e.preventDefault();
              }
            }}
            className="docid-field"
          />
        </div>
      </div>
      <Divider className="settings-divider" />
      <div
        className="analytic-engine-section"
        data-testid={`settings-tab-analytics-engine-analytics-engine-tooltip`}
      >
        <div
          className="docid-label"
          data-testid={`settings-tab-analytics-engine-analytics-engine-tooltip-btn`}
        >
          <Typography
            tabIndex={0}
            role="button"
            className="typo-text subhead3_size assign-users-title"
            data-testid={`settings-tab-analytics-engine`}
          >
            {t('projectManagement.settingsTab.analyticsEngine')}
          </Typography>
          <ArrTooltipWrapper
            trigger={
              <IconButton aria-label="Info" sx={{padding: 0}}>
                <InfoOutlinedIcon fontSize="medium" className="info-icon" />
              </IconButton>
            }
          >
            {t('projectManagement.settingsTab.analyticsEngineTooltip')}
          </ArrTooltipWrapper>
        </div>

        <div
          className="analytics-engine-form"
          data-testid={`settings-tab-div-btn`}
        >
          <Controller
            control={control}
            name="analyticsEngine"
            rules={{required: t('common.required')}}
            render={({field}) => (
              <ArrSingleSelectDropdown
                dropdownLabel={t(
                  'projectManagement.settingsTab.analyticsEngine'
                )}
                dropdownItems={engineOptions ?? []}
                selectedValues={field.value}
                onSelectionChange={(val) => field.onChange(val)}
                sizeVariant="large"
                dropdownItemKey="value"
                dropdownItemDisplay="label"
                disabled={type === 'edit'}
                dataTestId="asd-analyticsEngine"
              />
            )}
          />
          {errors.analyticsEngine && (
            <Typography
              className="error-text"
              tabIndex={0}
              role="button"
              data-testid={`settings-tab-typography`}
            >
              {errors.analyticsEngine.message?.toString()}
            </Typography>
          )}
        </div>
      </div>
      {showToast && (
        <ArrToast
          id="status-toast"
          title={
            toastType === ToastType.Success
              ? t('common.success')
              : t('common.error')
          }
          type={toastType}
          message={message}
          duration={10000}
          dismissable
          onClose={() => setShowToast(false)}
          closeIcon={<CloseToast />}
          position="bottom-right"
          className="activation-toast"
        />
      )}
    </div>
  );
};

export default SettingsTab;
